export interface FeedbackMessage {
  id: string
  authorType: 'external' | 'admin'
  body: string
  created: string
}

export interface FeedbackConversation {
  id: string
  status: 'open' | 'closed'
  lastMessageAt?: string
}

export interface FeedbackApiResponse {
  conversation: FeedbackConversation
  messages: FeedbackMessage[]
  /** Server digest of the message list; absent on older deployments. */
  digest?: string | null
}

/**
 * Lightweight poll response: no message bodies. `conversation` is null when
 * the user has no conversation on the server yet.
 */
export interface FeedbackMetaResponse {
  conversation: FeedbackConversation | null
  digest: string | null
}

export interface FeedbackCache {
  digest: string
  conversation: FeedbackConversation
  messages: FeedbackMessage[]
}

export const FEEDBACK_LAST_READ_KEY_PREFIX = 'jobcards.feedback.lastReadAt.'
export const FEEDBACK_CACHE_KEY_PREFIX = 'jobcards.feedback.cache.'

export function feedbackLastReadKey(userId: string): string {
  return `${FEEDBACK_LAST_READ_KEY_PREFIX}${userId}`
}

export function feedbackCacheKey(userId: string): string {
  return `${FEEDBACK_CACHE_KEY_PREFIX}${userId}`
}

/**
 * True when the meta response proves the cached messages are still current,
 * so the poller can skip the full fetch. `hasMessagesLoaded` guards the case
 * where a digest is cached but the messages themselves were never loaded.
 */
export function isCacheFresh(
  meta: FeedbackMetaResponse | null,
  cachedDigest: string | null,
  hasMessagesLoaded: boolean,
): boolean {
  return (
    meta !== null &&
    meta.digest !== null &&
    cachedDigest !== null &&
    meta.digest === cachedDigest &&
    hasMessagesLoaded
  )
}

export function latestMessageCreated(
  messages: FeedbackMessage[],
): string | null {
  let latest: string | null = null
  for (const message of messages) {
    if (latest === null || message.created > latest) {
      latest = message.created
    }
  }
  return latest
}

/**
 * Admin messages the user has not read yet. `lastReadAt` is the created
 * timestamp of the newest message the user has seen; null means nothing has
 * been read on this device yet.
 */
export function countUnread(
  messages: FeedbackMessage[],
  lastReadAt: string | null,
): number {
  return messages.filter(
    (message) =>
      message.authorType === 'admin' &&
      (lastReadAt === null || message.created > lastReadAt),
  ).length
}

/** Admin messages in `next` that were not present in `knownIds`. */
export function newAdminMessages(
  knownIds: ReadonlySet<string>,
  next: FeedbackMessage[],
): FeedbackMessage[] {
  return next.filter(
    (message) => message.authorType === 'admin' && !knownIds.has(message.id),
  )
}

async function readErrorMessage(
  response: Response,
  fallback: string,
): Promise<string> {
  try {
    const contentType = response.headers.get('content-type') || ''
    if (contentType.includes('application/json')) {
      const data = (await response.json()) as {
        error?: string
        message?: string
        details?: string
      }
      return data.error || data.message || data.details || fallback
    }
    const text = (await response.text()).trim()
    return text || fallback
  } catch {
    return fallback
  }
}

function buildHeaders(token: string | null): HeadersInit {
  const headers: HeadersInit = { 'Content-Type': 'application/json' }
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }
  return headers
}

export async function fetchFeedback(
  token: string | null,
): Promise<FeedbackApiResponse | null> {
  const response = await fetch('/api/feedback', { headers: buildHeaders(token) })
  if (response.status === 404) {
    return null
  }
  if (!response.ok) {
    const message = await readErrorMessage(
      response,
      `Failed to fetch feedback: ${response.statusText}`,
    )
    throw new Error(message)
  }
  return response.json()
}

/**
 * Fetch the lightweight poll meta. Returns null when the backend (or its
 * upstream) doesn't support the meta endpoint yet — callers should fall back
 * to a full fetch in that case.
 */
export async function fetchFeedbackMeta(
  token: string | null,
): Promise<FeedbackMetaResponse | null> {
  const response = await fetch('/api/feedback/meta', {
    headers: buildHeaders(token),
  })
  if (response.status === 404) {
    return null
  }
  if (!response.ok) {
    const message = await readErrorMessage(
      response,
      `Failed to fetch feedback meta: ${response.statusText}`,
    )
    throw new Error(message)
  }
  return response.json()
}

export async function sendFeedbackMessage(
  message: string,
  token: string | null,
): Promise<FeedbackApiResponse> {
  const response = await fetch('/api/feedback', {
    method: 'POST',
    headers: buildHeaders(token),
    body: JSON.stringify({ message }),
  })
  if (!response.ok) {
    const messageText = await readErrorMessage(
      response,
      `Failed to send message: ${response.statusText}`,
    )
    throw new Error(messageText)
  }
  return response.json()
}
